import React, { useCallback, useMemo } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { useParams } from 'react-router-dom';
import GridSection from 'components/_layouts/GridSection';
import PhxBadge from '_lex/_atoms/PhxBadge';
import useUrlParams from 'hooks/useUrlParams';
import { useEisScoredFields } from '../../_data_sources/useEisScoredFields';
import { useEisRuleDetailsContext } from '../../context';
import type { EisScoredFieldItem, EisScoringStatus } from '../../_types';

export type ScoredFieldsParams = {
  search?: string | null;
  dataType?: string | null;
  status?: EisScoringStatus | null;
};

const defaultParams: ScoredFieldsParams = {
  search: null,
  dataType: null,
  status: null,
};

function getScoringStatus(item: EisScoredFieldItem): EisScoringStatus {
  const scores = item.scores ?? [];
  const total = scores.length;
  if (total === 0) return 'NotScored';
  const withPoints = scores.filter((s) => s.points != null && s.points !== undefined).length;
  if (withPoints === 0) return 'NotScored';
  if (withPoints === total) return 'Scored';
  return 'PartiallyScored';
}

function getScoredAnswersText(item: EisScoredFieldItem): string {
  const scores = item.scores ?? [];
  const total = scores.length;
  const withPoints = scores.filter((s) => s.points != null && s.points !== undefined).length;
  return `${withPoints} of ${total}`;
}

export default function ScoredFields() {
  const { id } = useParams<{ id: string }>();
  const ruleId = id ? Number(id) : 0;
  const { rule, isLoading: isRuleLoading } = useEisRuleDetailsContext();
  const [params, setParams] = useUrlParams(defaultParams);

  const scoredFieldsQuery = useEisScoredFields(
    ruleId,
    { ruleVersionId: rule?.versionId },
    { enabled: !!ruleId && !!rule?.versionId },
    (err) => {
      console.error('Error fetching EIS scored fields:', err);
    },
  );

  const allItems = scoredFieldsQuery.data?.pages.flatMap((p) => p.content) ?? [];
  const totalCount = scoredFieldsQuery.data?.pages[0]?.count ?? 0;

  const filteredData = useMemo(() => {
    let list = allItems;
    const search = (params.search ?? '').trim().toLowerCase();
    if (search) {
      list = list.filter((item) => item.field?.label?.toLowerCase().includes(search));
    }
    if (params.dataType) {
      list = list.filter((item) => item.field?.type === params.dataType);
    }
    if (params.status) {
      list = list.filter((item) => getScoringStatus(item) === params.status);
    }
    return list;
  }, [allItems, params.search, params.dataType, params.status]);

  const updateParams = useCallback(
    (updated: Partial<ScoredFieldsParams>) => {
      setParams((prev: ScoredFieldsParams) => {
        const next = { ...prev, ...updated };
        document.body.dispatchEvent(new Event('params-changed'));
        return next;
      });
    },
    [setParams],
  );

  const onSearchChange = useCallback((val: string) => updateParams({ search: val?.trim() || null }), [updateParams]);

  const uniqueFieldTypes = useMemo(() => {
    const set = new Set<string>();
    allItems.forEach((item) => {
      if (item.field?.type) set.add(item.field.type);
    });
    return Array.from(set).sort();
  }, [allItems]);

  const columns = useMemo(
    () => ({
      defaultLabel: {
        header: t('performanceReporting.eis.details.scoredFields.columns.defaultLabel'),
        flex: 2,
        sortable: true,
        valueGetter: (_: unknown, row: EisScoredFieldItem) => row?.field?.label ?? '',
        renderer: (params: { row: EisScoredFieldItem }) => (
          <Typography variant='body1'>{params.row?.field?.label ?? '--'}</Typography>
        ),
      },
      fieldType: {
        header: t('performanceReporting.eis.details.scoredFields.columns.fieldType'),
        flex: 1.5,
        valueGetter: (_: unknown, row: EisScoredFieldItem) => row?.field?.type ?? '',
        renderer: (params: { row: EisScoredFieldItem }) => {
          const type = params.row?.field?.type;
          const label =
            type && t(`performanceReporting.eis.details.scoredFields.fieldType.${type}`)
              ? t(`performanceReporting.eis.details.scoredFields.fieldType.${type}`)
              : (type ?? '--');
          return <Typography variant='body1'>{label}</Typography>;
        },
      },
      templateSource: {
        header: t('performanceReporting.eis.details.scoredFields.columns.templateSource'),
        flex: 2,
        valueGetter: (_: unknown, row: EisScoredFieldItem) => row?.templateSource?.name ?? '',
        renderer: (params: { row: EisScoredFieldItem }) => (
          <Typography variant='body1'>{params.row?.templateSource?.name ?? '--'}</Typography>
        ),
      },
      scoredAnswers: {
        header: t('performanceReporting.eis.details.scoredFields.columns.scoredAnswers'),
        flex: 1,
        valueGetter: (_: unknown, row: EisScoredFieldItem) => getScoredAnswersText(row),
        renderer: (params: { row: EisScoredFieldItem }) => (
          <Typography variant='body1'>{getScoredAnswersText(params.row)}</Typography>
        ),
      },
      status: {
        header: t('performanceReporting.eis.details.scoredFields.columns.status'),
        flex: 1.2,
        valueGetter: (_: unknown, row: EisScoredFieldItem) => getScoringStatus(row),
        renderer: (params: { row: EisScoredFieldItem }) => {
          const status = getScoringStatus(params.row);
          const color: 'success' | 'warning' | 'default' =
            status === 'Scored' ? 'success' : status === 'PartiallyScored' ? 'warning' : 'default';
          const label =
            status === 'Scored'
              ? t('performanceReporting.eis.details.scoredFields.status.scored')
              : status === 'PartiallyScored'
                ? t('performanceReporting.eis.details.scoredFields.status.partiallyScored')
                : t('performanceReporting.eis.details.scoredFields.status.notScored');
          return (
            <PhxBadge
              color={color}
              size='sm'
            >
              {label}
            </PhxBadge>
          );
        },
      },
    }),
    [t],
  );

  const isLoading = isRuleLoading || scoredFieldsQuery.isLoading;
  const isError = scoredFieldsQuery.isError;

  return (
    <Stack
      spacing={4}
      sx={{ minHeight: 0 }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
          {t('performanceReporting.eis.details.scoredFields.errorLoading')}
        </Box>
      ) : filteredData.length === 0 ? (
        <Stack
          spacing={3}
          alignItems='center'
          sx={{ p: 4 }}
        >
          <Box
            component='img'
            src='/admin/img/performance-reporting/empty-eis.png'
            alt={t('performanceReporting.eis.details.scoredFields.emptyState.title')}
          />
          <Typography
            variant='body1'
            sx={{ fontWeight: 600 }}
          >
            {t('performanceReporting.eis.details.scoredFields.emptyState.title')}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('performanceReporting.eis.details.scoredFields.emptyState.subtitle')}
          </Typography>
        </Stack>
      ) : (
        <Box sx={{ minHeight: 400 }}>
          <GridSection
            columns={columns}
            data={filteredData}
            query={scoredFieldsQuery}
            totalCount={params.search || params.dataType || params.status ? filteredData.length : totalCount}
            gridKey={`scored-fields-${ruleId}-${params.search ?? ''}-${params.dataType ?? ''}-${params.status ?? ''}`}
          />
        </Box>
      )}
    </Stack>
  );
}
